import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Settings } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import MatchSettingsComponent from '../../scoring/MatchSettingsComponent';

/**
 * The gear-icon settings modal — ported from production's per-match
 * "Match Settings" modal (src/tournaments/tabs/ScheduleTab.jsx's
 * `matchSettingsModal` state + its "Use custom settings for this match" +
 * "Team Pairing" sections), generalized to also work at the ROUND level
 * (production has no round-level version — that part is new).
 *
 * Reuses `MatchSettingsComponent` (src/scoring/MatchSettingsComponent.jsx)
 * directly rather than copying it — it's already a clean, sport-config-
 * driven form with no live-tournament/API coupling of its own, unlike the
 * surrounding modal/save-handler code in ScheduleTab.jsx, which is what
 * actually got ported+adapted here (stripped of `tournamentScheduleAPI`
 * calls, `canSchedule` access-control gating, and the live `getTeamRoster`
 * fetch — this module already has every roster in memory, no API needed).
 *
 * `scope`:
 * - 'match' — full modal: court + custom settings + Team Pairing (which
 *   specific roster players play THIS match).
 * - 'round' — court + custom settings only. No Team Pairing: there's no
 *   single sensible player choice across every match in a round, since
 *   different matches in the same round involve different teams entirely.
 *   When this round already has matches with their own row-level override,
 *   shows two Apply variants instead of one (see `conflictingMatchCount`) —
 *   the caller (RoundSection.jsx) decides what "apply to all" actually
 *   clears, since only it knows this round's row keys.
 */
const MatchSettingsModal = ({
  isOpen,
  onClose,
  scope,
  sportType,
  courts = [],
  initialOverride,
  tournamentMatchSettings,
  conflictingMatchCount = 0,
  slotA,
  slotB,
  onApply,
}) => {
  const { theme } = useTheme();
  const [court, setCourt] = useState('');
  const [useCustomSettings, setUseCustomSettings] = useState(false);
  const [settings, setSettings] = useState({});
  const [selectedPlayersA, setSelectedPlayersA] = useState([]);
  const [selectedPlayersB, setSelectedPlayersB] = useState([]);

  // The modal stays mounted between opens (caller just toggles `isOpen`),
  // so this re-seeds from whatever override is actually current each time
  // it's reopened, instead of showing a stale draft from the last time.
  // `settings` seeds from the existing override when there is one (an
  // organizer editing an already-customized match/round sees THEIR values,
  // unchanged) — otherwise from this tournament's own configured match
  // settings (`tournamentMatchSettings`, see useScheduleBuilder.js), so
  // checking "Use custom settings" for the first time starts from what the
  // tournament is actually set to instead of MatchSettingsComponent's
  // generic sport-wide defaults, and the organizer only has to touch the
  // one field they actually want to override.
  useEffect(() => {
    if (!isOpen) return;
    setCourt(initialOverride?.court || '');
    setUseCustomSettings(initialOverride?.useCustomSettings || false);
    setSettings(initialOverride?.settings || tournamentMatchSettings || {});
    setSelectedPlayersA(initialOverride?.playersA || []);
    setSelectedPlayersB(initialOverride?.playersB || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  // Team Pairing only makes sense once a concrete participantsType is
  // known — with custom settings off, this module has no other baseline to
  // fall back on (unlike production, which always has the tournament's own
  // participantsType as a default) — and only for a side that's a real team
  // roster bigger than what that participantsType needs.
  const requiredPlayerCount = useCustomSettings && scope === 'match'
    ? (settings.participantsType === 'doubles' ? 2 : settings.participantsType === 'singles' ? 1 : null)
    : null;
  const showPairingA = requiredPlayerCount != null && Array.isArray(slotA?.roster) && slotA.roster.length > requiredPlayerCount;
  const showPairingB = requiredPlayerCount != null && Array.isArray(slotB?.roster) && slotB.roster.length > requiredPlayerCount;

  const togglePlayer = (player, selected, setSelected) => {
    const isSelected = selected.some((p) => p.id === player.id);
    if (isSelected) setSelected(selected.filter((p) => p.id !== player.id));
    else if (selected.length < requiredPlayerCount) setSelected([...selected, player]);
  };

  const buildOverride = () => ({
    court: court || undefined,
    useCustomSettings,
    settings: useCustomSettings ? settings : undefined,
    playersA: showPairingA ? selectedPlayersA : undefined,
    playersB: showPairingB ? selectedPlayersB : undefined,
  });

  const apply = (applyToAllInRound) => {
    onApply(buildOverride(), applyToAllInRound);
    onClose();
  };

  // Portaled to document.body — this modal is opened from deep inside a
  // per-row gear icon (MatchRow.jsx), nested under GeneratedScheduleStep's
  // `tf-animate-in` wrapper. That class touches `transform` in its
  // animation, which leaves a non-`none` computed transform (identity
  // matrix) even after the animation finishes — creating a containing
  // block that traps `position: fixed` descendants inside the page's own
  // scrolled content instead of the true viewport. Confirmed bug pattern
  // from this module's earlier reorder-feature work; portaling avoids it
  // instead of rediscovering it here.
  return createPortal(
    // stopPropagation here, not just on the backdrop — this modal is
    // rendered as a JSX child of the row/round it belongs to (MatchRow.jsx /
    // RoundSection.jsx), and a portal only relocates where a node lives in
    // the DOM, not where it sits in the REACT tree that synthetic events
    // bubble through. Without this, clicking "Apply" (or anything else in
    // here) would also fire the row's own onClick (opening Match Detail)
    // once this modal closes and the click keeps bubbling.
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border ${theme.border} ${theme.card} ${theme.shadow}`}>
        <div className={`flex shrink-0 items-center justify-between border-b ${theme.border} px-6 py-4`}>
          <div className={`flex items-center gap-2 text-base font-bold ${theme.text}`}>
            <Settings size={18} />
            {scope === 'round' ? 'Round Settings' : 'Match Settings'}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${theme.buttonGhost}`}
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {courts.length > 0 && (
            <div className="mb-4">
              <label className={`mb-2 block text-sm font-medium ${theme.text}`}>Court / Field</label>
              <select
                value={court}
                onChange={(e) => setCourt(e.target.value)}
                className={`w-full rounded-lg border px-3 py-2 text-sm ${theme.input}`}
              >
                <option value="">Auto-assign</option>
                {courts.map((c) => (
                  <option key={c.id} value={c.label}>{c.label}</option>
                ))}
              </select>
            </div>
          )}

          <label className="mb-4 flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={useCustomSettings}
              onChange={(e) => setUseCustomSettings(e.target.checked)}
              className="h-4 w-4 rounded text-blue-600"
            />
            <span className={`text-sm ${theme.text}`}>
              {scope === 'round' ? 'Use custom settings for this round' : 'Use custom settings for this match'}
            </span>
          </label>

          {useCustomSettings && (
            <MatchSettingsComponent
              sportType={sportType}
              initialSettings={settings}
              onSettingsChange={(next) => setSettings((prev) => ({ ...prev, ...next }))}
              participantsTypeValue={settings.participantsType}
              onParticipantsTypeChange={(participantsType) => setSettings((prev) => ({ ...prev, participantsType }))}
              showNextButton={false}
              compact
            />
          )}

          {scope === 'match' && (showPairingA || showPairingB) && (
            <div className="mt-4">
              <h4 className={`mb-1 text-sm font-semibold ${theme.text}`}>Team Pairing</h4>
              <p className={`mb-2 text-xs ${theme.textMuted}`}>Select which players from each team play this match.</p>
              <div className="grid grid-cols-2 gap-3">
                {showPairingA && (
                  <div>
                    <div className={`mb-1 truncate text-xs font-medium ${theme.text}`}>{slotA.name}</div>
                    <div className="space-y-1">
                      {slotA.roster.map((p) => (
                        <label key={p.id} className="flex cursor-pointer items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedPlayersA.some((x) => x.id === p.id)}
                            onChange={() => togglePlayer(p, selectedPlayersA, setSelectedPlayersA)}
                            className="h-4 w-4 rounded text-blue-600"
                          />
                          <span className={`truncate text-sm ${theme.text}`}>{p.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {showPairingB && (
                  <div>
                    <div className={`mb-1 truncate text-xs font-medium ${theme.text}`}>{slotB.name}</div>
                    <div className="space-y-1">
                      {slotB.roster.map((p) => (
                        <label key={p.id} className="flex cursor-pointer items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedPlayersB.some((x) => x.id === p.id)}
                            onChange={() => togglePlayer(p, selectedPlayersB, setSelectedPlayersB)}
                            className="h-4 w-4 rounded text-blue-600"
                          />
                          <span className={`truncate text-sm ${theme.text}`}>{p.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {scope === 'round' && conflictingMatchCount > 0 ? (
            <div className="mt-5 space-y-2">
              <p className={`text-xs ${theme.textMuted}`}>
                {conflictingMatchCount} match{conflictingMatchCount === 1 ? '' : 'es'} in this round already{' '}
                {conflictingMatchCount === 1 ? 'has' : 'have'} its own settings override.
              </p>
              <button
                type="button"
                onClick={() => apply(false)}
                className={`w-full rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${theme.buttonGhost} border ${theme.border}`}
              >
                Keep individual match overrides
              </button>
              <button
                type="button"
                onClick={() => apply(true)}
                className={`w-full rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99]`}
              >
                Override all matches in this round
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => apply(false)}
              className={`mt-5 w-full rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99]`}
            >
              Apply
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MatchSettingsModal;
